import {
  changePenSmoothing,
  penSmoothing,
  supportsPenSmoothing,
} from "../native/pen-smoothing";
import {
  changeCorners,
  cornerLimit,
  cornerRadius,
  supportsCorners,
} from "../native/corners";
import type { NativeSession } from "../native/session";
import type { NativePointer } from "../native/pointer";
import type { SceneNode } from "../scene/model";
import { changeAppearance } from "../native/appearance";
import { changePolygon } from "../native/shapes";

type Field =
  "opacity" | "stroke-width" | "sides" | "depth" | "radius" | "pen-smoothing";
type Control = {
  source: HTMLInputElement;
  range: HTMLInputElement;
  field: Field;
};
const settings: Record<Field, [number, number]> = {
  radius: [0, 100],
  "pen-smoothing": [0, 100],
  opacity: [0, 100],
  "stroke-width": [0, 100],
  sides: [3, 24],
  depth: [10, 90],
};

/** Slider previews share scene commands with exact inputs and commit once per gesture. */
export class NativeRanges {
  private controls: Control[] = [];
  private blocked: Control | null = null;
  private edit: {
    control: Control;
    nodes: SceneNode[];
    generation: number;
    revision: number;
  } | null = null;
  constructor(
    private session: NativeSession,
    private pointer: NativePointer,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
  ) {
    for (const prefix of ["native-", "native-compact-"]) {
      const fields: Field[] =
        prefix === "native-"
          ? ["opacity", "depth", "radius", "pen-smoothing"]
          : [
              "opacity",
              "stroke-width",
              "sides",
              "depth",
              "radius",
              "pen-smoothing",
            ];
      for (const field of fields) {
        const source = document.getElementById(
          prefix + field,
        ) as HTMLInputElement;
        const label = source.parentElement!;
        const range = document.createElement("input");
        range.type = "range";
        range.id = source.id + "-range";
        range.min = String(settings[field][0]);
        range.max = String(settings[field][1]);
        range.step = "1";
        range.setAttribute("aria-label", label.textContent!.trim());
        label.classList.add("native-range-heading");
        const container = document.createElement("div");
        container.className =
          "native-range-control" +
          (prefix === "native-compact-" ? " is-vertical" : "");
        label.before(container);
        container.append(label, range);
        if (prefix === "native-compact-")
          container.parentElement!.classList.add("native-range-layout");
        const control = { source, range, field };
        this.controls.push(control);
        if (field === "radius" || field === "pen-smoothing") {
          source.oninput = () => {
            this.blocked = null;
            if (source.value.trim())
              this.preview(control, Number(source.value));
          };
          source.onchange = () => {
            this.finish();
            source.blur();
            this.changed();
          };
          source.onblur = () => {
            this.finish();
            this.changed();
          };
          source.onkeydown = (event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              this.cancel();
              source.blur();
              this.changed();
            }
          };
        }
        range.onpointerdown = () => {
          this.blocked = null;
          this.begin(control);
        };
        range.oninput = () => this.preview(control);
        range.onchange = () => {
          this.preview(control);
          this.finish();
        };
        range.onpointercancel = () => this.cancel();
        range.onblur = () => this.finish();
        range.onkeydown = (event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            this.cancel();
            range.blur();
          } else this.blocked = null;
        };
      }
    }
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (
          this.edit &&
          event.target !== this.edit.control.range &&
          event.target !== this.edit.control.source
        )
          this.finish();
      },
      true,
    );
    window.addEventListener("pointerup", () => {
      // Native range change may follow pointerup; finish after that event completes.
      queueMicrotask(() => this.finish());
    });
    window.addEventListener("blur", () => this.cancel());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.cancel();
    });
  }
  get active() {
    return this.edit !== null;
  }
  private begin(control: Control) {
    if (this.edit?.control === control) return true;
    if (this.pointer.active || control.range.disabled || this.session.gesturing)
      return false;
    this.finish();
    this.edit = {
      control,
      nodes: this.session.selectedNodes,
      generation: this.session.generation,
      revision: this.session.revision,
    };
    this.session.begin();
    return true;
  }
  private valid() {
    return (
      this.edit &&
      this.edit.generation === this.session.generation &&
      this.edit.revision === this.session.revision
    );
  }
  private preview(control: Control, requested?: number) {
    if (this.blocked === control) return;
    if (!this.begin(control)) return;
    if (!this.valid()) {
      this.edit = null;
      return;
    }
    try {
      const value = requested ?? Number(control.range.value),
        nodes = this.edit!.nodes;
      const changed =
        control.field === "pen-smoothing"
          ? changePenSmoothing(nodes, value)
          : control.field === "radius"
            ? changeCorners(nodes, value)
            : control.field === "depth" || control.field === "sides"
              ? changePolygon(
                  nodes,
                  control.field === "depth" ? "innerRatio" : "sides",
                  control.field === "depth" ? (100 - value) / 100 : value,
                )
              : changeAppearance(
                  nodes,
                  control.field === "opacity" ? "opacity" : "strokeWidth",
                  control.field === "opacity" ? value / 100 : value,
                );
      this.session.replaceMany(changed);
      this.changed();
    } catch (error) {
      this.cancel();
      this.message((error as Error).message, true);
    }
  }
  private finish() {
    if (!this.edit) return;
    if (this.valid()) this.session.finish();
    this.edit = null;
    this.changed();
  }
  cancel() {
    if (!this.edit) return;
    this.blocked = this.edit.control;
    if (this.valid()) this.session.cancel();
    this.edit = null;
    this.changed();
  }
  render() {
    for (const { source, range, field } of this.controls) {
      if (field === "radius") {
        const nodes = this.session.selectedNodes.filter(supportsCorners);
        const values = nodes.map(cornerRadius);
        source.disabled = this.pointer.active || !nodes.length;
        source.max = String(
          Math.max(1, ...nodes.map((n) => Math.ceil(cornerLimit(n)))),
        );
        range.max = source.max;
        source.placeholder = "Разные";
        if (document.activeElement !== source)
          source.value =
            values.length && values.every((v) => v === values[0])
              ? String(Math.round(values[0]! * 10000) / 10000)
              : "";
        range.parentElement!.hidden = !nodes.length;
        range.hidden =
          source.id === "native-radius" &&
          !nodes.some((n) => n.kind === "image");
      }
      if (field === "pen-smoothing") {
        const nodes = this.session.selectedNodes.filter(supportsPenSmoothing),
          values = nodes.map(penSmoothing);
        source.disabled = this.pointer.active || !nodes.length;
        source.placeholder = "Разные";
        if (document.activeElement !== source)
          source.value =
            values.length && values.every((v) => v === values[0])
              ? String(values[0])
              : "";
        range.parentElement!.hidden = !nodes.length;
      }
      range.disabled = source.disabled;
      const mixed = !source.value;
      if (this.edit?.control.range !== range)
        range.value = mixed
          ? String((Number(range.min) + Number(range.max)) / 2)
          : source.value;
      range.setAttribute(
        "aria-valuetext",
        mixed
          ? "Разные значения"
          : source.value +
              (field === "opacity" || field === "depth" ? "%" : ""),
      );
      range.dataset.mixed = String(mixed);
      if (field === "depth")
        range.parentElement!.hidden = !this.session.selectedNodes.some(
          (n) => n.kind === "polygon" && n.geometry.star,
        );
    }
  }
}
